import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../utils/net-api';
import './NewHomePage.css';

const NewHomePage = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [carouselData, setCarouselData] = useState([]);
    const [loading, setLoading] = useState(true);
    const totalSlides = 5;
    const [isCarouselFixed, setIsCarouselFixed] = useState(false);
    const [isPC, setIsPC] = useState(window.innerWidth > 834);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
    
    // 使用 useRef 保持状态一致性，避免闭包陷阱
    const currentSlideRef = useRef(currentSlide);
    const isAnimatingRef = useRef(isAnimating);
    const isCarouselFixedRef = useRef(isCarouselFixed);
    const isProcessingRef = useRef(false); // 防止重复处理
    const lastWheelTimeRef = useRef(0); // 记录上次wheel事件时间
    const touchStartYRef = useRef(0);
    const touchMoveAccumulatedRef = useRef(0); // 累计移动距离
    
    // 同步 ref 值
    useEffect(() => {
        currentSlideRef.current = currentSlide;
    }, [currentSlide]);
    
    useEffect(() => {
        isAnimatingRef.current = isAnimating;
    }, [isAnimating]);
    
    useEffect(() => {
        isCarouselFixedRef.current = isCarouselFixed;
    }, [isCarouselFixed]);

    useEffect(() => {
        document.title = "Marlo - Home";
        console.log('NewHomePage loaded, currentSlide:', currentSlide);
        loadCarouselData();
        
        // 监听窗口大小变化
        const handleResize = () => {
            setIsPC(window.innerWidth > 834);
            setIsMobile(window.innerWidth <= 480);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 加载轮播数据
    const loadCarouselData = async () => {
        try {
            const response = await axios.get(API.homeCarousel);
            if (response.data && response.data.Succeed) {
                setCarouselData(response.data.Data || []);
            } else {
                console.error('Failed to load carousel data:', response.data?.Message);
                // 使用默认数据作为fallback
                setCarouselData([]);
            }
        } catch (error) {
            console.error('Failed to load carousel data:', error);
            // 使用默认数据作为fallback
            setCarouselData([]);
        } finally {
            setLoading(false);
        }
    };

    // 格式化日期时间
    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'TBD';
        const date = new Date(timestamp * 1000); // 转换为毫秒
        const options = { 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        };
        return date.toLocaleDateString('en-US', options).replace(',', ',');
    };

    // 获取轮播数据，如果没有足够数据则使用默认数据填充
    const getSlideData = (index) => {
        const defaultData = [
            { title: "Grab a coffee at local coffee shop with me", location: "FutureWard", description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut", attendeeCount: "34+" },
            { title: "Join our weekend hiking adventure", location: "Golden Gate Park", description: "Join us for an exciting hiking adventure in the beautiful Golden Gate Park", attendeeCount: "12+" },
            { title: "Photography walk in the city center", location: "Union Square", description: "Capture the beauty of the city with fellow photography enthusiasts", attendeeCount: "8+" },
            { title: "Book club meeting this Thursday", location: "Public Library", description: "Discuss the latest bestseller with our friendly book club members", attendeeCount: "15+" },
            { title: "Cooking class for beginners", location: "Community Center", description: "Learn basic cooking skills in a fun and supportive environment", attendeeCount: "6+" }
        ];

        if (carouselData && carouselData[index]) {
            const wave = carouselData[index];
            return {
                title: wave.Title || defaultData[index].title,
                location: wave.DetailedLocation || defaultData[index].location,
                description: wave.Body || defaultData[index].description,
                dateTime: `${wave.EventDateStr}, ${wave.EventTimeStr}` || formatDateTime(wave.EventStartTimestamp),
                attendeeCount: wave.Behaviors && wave.Behaviors.length > 0 ? `${wave.Behaviors.length}+` : defaultData[index].attendeeCount,
                id: wave.ID,
                imageUrl: wave.Media && wave.Media.length > 0 ? wave.Media[0].FilePath : null
            };
        }
        
        return {
            ...defaultData[index],
            dateTime: "September 30, 19:30", // 默认时间
            id: null,
            imageUrl: null
        };
    };

    useEffect(() => {
        console.log('CurrentSlide changed to:', currentSlide);
    }, [currentSlide]);

    // 统一的slide切换函数
    const changeSlide = useCallback((newIndex) => {
        if (isProcessingRef.current || isAnimatingRef.current) return false;
        if (newIndex < 0 || newIndex >= totalSlides) return false;
        if (newIndex === currentSlideRef.current) return false;
        
        isProcessingRef.current = true;
        setIsAnimating(true);
        
        setTimeout(() => {
            setCurrentSlide(newIndex);
        }, 100);
        
        setTimeout(() => {
            setIsAnimating(false);
            isProcessingRef.current = false;
        }, 700);
        
        return true;
    }, [totalSlides]);

    // 轮播区域ref
    const thirdSectionRef = useRef(null);
    const isLockedRef = useRef(false); // 是否锁定在轮播区域
    const unlockCooldownRef = useRef(false); // 解锁冷却
    
    // 使用 Intersection Observer 检测轮播区域
    useEffect(() => {
        const thirdSection = thirdSectionRef.current;
        if (!thirdSection) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // 当轮播区域占据视口80%以上时，准备吸附
                    if (entry.intersectionRatio >= 0.8 && !isLockedRef.current && !unlockCooldownRef.current) {
                        // 滚动到轮播区域顶部
                        thirdSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            },
            { threshold: [0.8] }
        );

        observer.observe(thirdSection);

        return () => observer.disconnect();
    }, []);

    // 轮播区域的事件处理
    useEffect(() => {
        const thirdSection = thirdSectionRef.current;
        if (!thirdSection) return;

        // 检查是否有弹窗打开
        const isMenuOpen = () => document.body.classList.contains('menu-open-blur');
        
        // 检查轮播是否在视口中心
        const isCarouselCentered = () => {
            const rect = thirdSection.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            // 轮播区域顶部在视口上半部分，底部在视口下半部分
            return rect.top <= viewportHeight * 0.1 && rect.bottom >= viewportHeight * 0.9;
        };

        // PC端 wheel 事件
        const handleWheel = (e) => {
            if (isMenuOpen()) return; // 弹窗打开时不处理
            if (!isCarouselCentered()) return;
            if (unlockCooldownRef.current) return; // 冷却中，允许自然滚动
            
            const delta = e.deltaY;
            const current = currentSlideRef.current;

            // 检查是否在边界且继续滑动
            const isAtFirstAndScrollingUp = current === 0 && delta < 0;
            const isAtLastAndScrollingDown = current === totalSlides - 1 && delta > 0;
            
            if (isAtFirstAndScrollingUp || isAtLastAndScrollingDown) {
                // 边界情况，不阻止默认滚动，允许页面滚动离开
                unlockCooldownRef.current = true;
                setTimeout(() => { unlockCooldownRef.current = false; }, 1500);
                return;
            }
            
            // 非边界情况，阻止默认滚动
            e.preventDefault();
            
            const now = Date.now();
            if (now - lastWheelTimeRef.current < 500) return;
            if (isProcessingRef.current || isAnimatingRef.current) return;
            
            if (delta > 0 && current < totalSlides - 1) {
                lastWheelTimeRef.current = now;
                changeSlide(current + 1);
            } else if (delta < 0 && current > 0) {
                lastWheelTimeRef.current = now;
                changeSlide(current - 1);
            }
        };

        // 触摸事件
        let isTouchInCarousel = false;
        let shouldBlockScroll = true;
        
        const handleTouchStart = (e) => {
            if (isMenuOpen()) return; // 弹窗打开时不处理
            touchStartYRef.current = e.touches[0].clientY;
            isTouchInCarousel = isCarouselCentered() && !unlockCooldownRef.current;
            shouldBlockScroll = true;
        };

        const handleTouchMove = (e) => {
            if (!isTouchInCarousel || !shouldBlockScroll) return;
            
            // 实时检测滑动方向和边界
            const currentY = e.touches[0].clientY;
            const deltaY = touchStartYRef.current - currentY;
            const current = currentSlideRef.current;
            
            const isAtFirstAndScrollingUp = current === 0 && deltaY < -30;
            const isAtLastAndScrollingDown = current === totalSlides - 1 && deltaY > 30;
            
            if (isAtFirstAndScrollingUp || isAtLastAndScrollingDown) {
                // 边界情况，允许自然滚动
                shouldBlockScroll = false;
                unlockCooldownRef.current = true;
                setTimeout(() => { unlockCooldownRef.current = false; }, 1500);
                return;
            }
            
            e.preventDefault();
        };

        const handleTouchEnd = (e) => {
            if (!isTouchInCarousel) return;
            if (!shouldBlockScroll) {
                isTouchInCarousel = false;
                return;
            }
            if (isProcessingRef.current || isAnimatingRef.current) return;
            
            const touchEndY = e.changedTouches[0].clientY;
            const deltaY = touchStartYRef.current - touchEndY;

            if (Math.abs(deltaY) < 50) return;

            const current = currentSlideRef.current;

            if (deltaY > 0 && current < totalSlides - 1) {
                changeSlide(current + 1);
            } else if (deltaY < 0 && current > 0) {
                changeSlide(current - 1);
            }
            
            isTouchInCarousel = false;
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [totalSlides, changeSlide]);

    const handleMoreEventsClick = () => {
        navigate('/event');
    };

    const handleDotClick = (index) => {
        changeSlide(index);
    };

    return (
        <div className="new-home-page">
            <Header />
            
            <main className="home-main">
                {/* First module */}
                <div className="hero-section">
                    {/* 光晕背景效果 */}
                    <div className="hero-glow-effect"></div>
                    <div className="hero-glow-effect2"></div>
                    
                    <img 
                        src={require('../images/home/card1.png')} 
                        alt="Card 1" 
                        className="hero-card1"
                    />
                    <img 
                        src={require('../images/home/card2.png')} 
                        alt="Card 2" 
                        className="hero-card2"
                    />
                    <img 
                        src={require('../images/home/card3.png')} 
                        alt="Card 3" 
                        className="hero-card3"
                    />
                    <div className="hero-text-group">
                        <div className="hero-text-line1">MARLO</div>
                        <div className="hero-text-line2">Find your</div>
                        <div className="hero-text-line3">people nearby</div>
                        <div className="hero-text-line4">
                            Marlo isn't just about events or matching—it's about instantly connecting real people <br />through real activities nearby.
                        </div>
                    </div>
                    <img 
                        src={require('../images/home/cards.png')} 
                        alt="Mobile Cards" 
                        className="hero-mobile-cards"
                    />
                </div>
                
                {/* Second module */}
                <div className="second-section">
                    <div className="second-overlay">
                        <div className="second-text-group">
                            <div className="second-text-line1">
                                <span className="pc-text">Join us in shaping the next evolution of social connection.</span>
                                <span className="mobile-text">
                                    <span>Join us in shaping</span><br/>
                                    <span>the next evolution</span><br/>
                                    <span>of social</span><br/>
                                    <span>connection.</span>
                                </span>
                            </div>
                            <div className="second-text-line2" onClick={() => navigate('/business')} style={{ cursor: 'pointer' }}>
                                <div className="second-text-container">
                                    <span className="second-text">Business Solutions</span>
                                    <div className="second-text-underline"></div>
                                </div>
                                <img 
                                    src={require('../images/home/right.png')} 
                                    alt="Right Arrow" 
                                    className="second-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Third module - 轮播区域 */}
                <div className="third-section" ref={thirdSectionRef}>
                    {/* 动态模糊背景 - 为每个slide预渲染背景 */}
                    {[0, 1, 2, 3, 4].map((bgIndex) => (
                        <div 
                            key={`bg-${bgIndex}`}
                            className="third-section-bg"
                            style={{
                                backgroundImage: getSlideData(bgIndex).imageUrl 
                                    ? `url(${getSlideData(bgIndex).imageUrl})` 
                                    : 'url(../images/home/bg3.png)',
                                opacity: bgIndex === currentSlide ? 1 : 0,
                                transition: 'opacity 0.7s ease-in-out'
                            }}
                        />
                    ))}
                    <div className="third-carousel-container">
                        {[0, 1, 2, 3, 4].map((slideIndex) => (
                            <div 
                                key={slideIndex}
                                className={`third-content ${slideIndex === currentSlide ? 'active' : ''} ${isAnimating ? 'animating' : ''}`}
                                style={{
                                    transform: slideIndex === currentSlide ? 'translateY(0)' : 
                                              slideIndex < currentSlide ? 'translateY(-100%)' : 'translateY(100%)',
                                    opacity: slideIndex === currentSlide ? 1 : 0
                                }}
                            >
                                {/* First block */}
                                <div className="third-first-block">
                                    <div className="third-title">
                                        {getSlideData(slideIndex).title}
                                    </div>
                                    <div className="third-meta-row">
                                        <div className="third-meta-item">
                                            <img 
                                                src={require('../images/home/time.png')} 
                                                alt="Time" 
                                                className="third-meta-icon"
                                            />
                                            <span className="third-meta-text">
                                                {getSlideData(slideIndex).dateTime}
                                            </span>
                                        </div>
                                        <div className="third-meta-item">
                                            <img 
                                                src={require('../images/home/addr.png')} 
                                                alt="Location" 
                                                className="third-meta-icon"
                                            />
                                            <span className="third-meta-text">
                                                {getSlideData(slideIndex).location}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="third-divider"></div>
                                    <div className="third-description">
                                        {getSlideData(slideIndex).description}
                                    </div>
                                    <div className="third-avatars">
                                        {(() => {
                                            const slideData = getSlideData(slideIndex);
                                            const behaviors = carouselData && carouselData[slideIndex] ? carouselData[slideIndex].Behaviors : [];
                                            const attendeeCount = behaviors.length;
                                            
                                            if (attendeeCount === 0) {
                                                // 没有参与者时显示默认头像
                                                return (
                                                    <div className="third-avatar third-avatar-default">
                                                        <img 
                                                            src="/images/avatar_no.png" 
                                                            alt="No attendees"
                                                            className="third-avatar-image"
                                                        />
                                                    </div>
                                                );
                                            } else {
                                                // 有参与者时显示头像
                                                const limit = Math.min(attendeeCount, 4);
                                                const avatars = [];
                                                
                                                for (let i = 0; i < limit; i++) {
                                                    const user = behaviors[i].User;
                                                    avatars.push(
                                                        <div key={i} className="third-avatar" style={{ zIndex: limit - i }}>
                                                            <img 
                                                                src={user?.ProfilePicture || require('../images/avatar_default.png')} 
                                                                alt={user?.Name || 'User'}
                                                                className="third-avatar-image"
                                                                onError={(e) => {
                                                                    e.target.onerror = null; // 防止无限循环
                                                                    e.target.src = require('../images/avatar_default.png');
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                }
                                                
                                                // 如果超过4个参与者，显示"+数字"
                                                if (attendeeCount > 4) {
                                                    avatars.push(
                                                        <div key="more" className="third-avatar third-avatar-count" style={{ zIndex: 0 }}>
                                                            {attendeeCount - 4}+
                                                        </div>
                                                    );
                                                }
                                                
                                                return avatars;
                                            }
                                        })()}
                                    </div>
                                    <button 
                                        className="third-learn-more"
                                        onClick={() => {
                                            const slideData = getSlideData(slideIndex);
                                            if (slideData.id) {
                                                navigate(`/event/${slideData.id}`);
                                            }
                                        }}
                                    >
                                        Learn More
                                    </button>
                                    
                                    {/* More Events - 移到 first-block 内部 */}
                                    <div className="third-more-events-inline" onClick={handleMoreEventsClick}>
                                        <div className="third-more-text-container">
                                            <span className="third-more-text">More Events</span>
                                            <div className="third-more-underline"></div>
                                        </div>
                                        <img 
                                            src={require('../images/home/right.png')} 
                                            alt="Right Arrow" 
                                            className="third-more-icon"
                                        />
                                    </div>
                                </div>
                                
                                {/* Second block */}
                                <div className="third-second-block">
                                    {getSlideData(slideIndex).imageUrl && (
                                        <img 
                                            src={getSlideData(slideIndex).imageUrl} 
                                            alt={getSlideData(slideIndex).title}
                                            className="third-event-image"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {/* Carousel Controls */}
                    <div className="third-carousel-controls">
                        {[0, 1, 2, 3, 4].map((index) => (
                            <div 
                                key={index}
                                className={`carousel-dot ${index === currentSlide ? 'carousel-dot-active' : ''}`}
                                onClick={() => handleDotClick(index)}
                            ></div>
                        ))}
                    </div>
                    
                    {/* More Events */}
                    <div className="third-more-events" onClick={handleMoreEventsClick}>
                        <div className="third-more-text-container">
                            <span className="third-more-text">More Events</span>
                            <div className="third-more-underline"></div>
                        </div>
                        <img 
                            src={require('../images/home/right.png')} 
                            alt="Right Arrow" 
                            className="third-more-icon"
                        />
                    </div>
                </div>
                
                {/* Fourth module - Download */}
                <div className="fourth-section">
                    <div className="fourth-content">
                        {/* Top notification */}
                        <div className="fourth-notification">
                        </div>
                        
                        {/* Main content */}
                        <div className="fourth-main">
                            <div className="fourth-title-group">
                                <div className="fourth-title-line1">
                                    <span className="fourth-title-text">Download</span>
                                    <span className="fourth-title-gradient">Marlo</span>
                                    <span className="fourth-title-text">to make friends</span>
                                </div>
                                <div className="fourth-title-line2">and have fun.</div>
                            </div>
                            
                            <img 
                                src={require('../images/home/download.png')} 
                                alt="Download" 
                                className="fourth-download-icon"
                                onClick={() => window.open('https://apps.apple.com/us/app/marlo-connect-meet-nearby/id6450737280?l=zh-Hans-CN', '_blank')}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default NewHomePage;
